import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { getAdjacentProjects } from '../../utils/projectNavigation';
import {
  headerBrandLinkClass,
  headerPrivateBadgeClass,
  headerPrimaryNavLinkClass,
  headerAdjacentProjectLinkClass,
  headerAdjacentProjectArrowClass,
  headerAdjacentProjectUnderlineClass,
  headerAdjacentProjectDotClass,
  headerMobilePrimaryLinkClass,
  headerMobileSectionLabelClass,
  headerMobileAdjacentProjectLinkClass,
  primaryPageShellClass,
} from '../../tokens';

// V3 header: brand on the left, adjacent project titles in the centre on case
// studies; nav on the right. The category filter lives on the landing page
// itself, beside the work it filters, rather than up here in the chrome.

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [darkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      const next = !prev;
      document.body.style.overflow = next ? 'hidden' : '';
      return next;
    });
  };

  const closeMenu = () => {
    setMenuOpen(false);
    document.body.style.overflow = '';
  };

  const handleWorkClick = (e) => {
    e.preventDefault();
    closeMenu();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const isWorkActive =
    location.pathname === '/' || location.pathname === '/workSection';
  const isAboutActive = location.pathname.toLowerCase().includes('/about');

  const projectNav = getAdjacentProjects(location.pathname);

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 right-0 z-50
          h-[72px] flex items-center
          transition-all duration-300
          ${scrolled
            ? 'bg-white/90 dark:bg-black/90 backdrop-blur-md border-b border-border-subtle dark:border-neutral-900'
            : 'bg-transparent'}
        `}
      >
        <div className={`${primaryPageShellClass} grid grid-cols-[1fr_auto_1fr] items-center gap-6`}>
          {/* LEFT — brand */}
          <div className="flex items-center gap-4 min-w-0">
            <NavLink
              to="/"
              className={headerBrandLinkClass}
              data-cursor="pointer"
            >
              Shareef Jasim
            </NavLink>
            {location.pathname.startsWith("/for/") && (
              <span
                className={headerPrivateBadgeClass}
                title="Private share link"
              >
                Private
              </span>
            )}
          </div>

          {/* CENTER — prev/next project titles (case studies only) */}
          <div className="hidden lg:flex items-center justify-center min-w-0">
            {projectNav && (
              <nav
                aria-label="Adjacent projects"
                className="flex items-center justify-center gap-5 xl:gap-8 max-w-[min(560px,52vw)]"
              >
                <Link
                  to={projectNav.prev.to}
                  className={`${headerAdjacentProjectLinkClass} justify-end`}
                  title={`Previous project · ${projectNav.prev.title}`}
                  data-cursor="pointer"
                  aria-label={`Previous project: ${projectNav.prev.title}`}
                >
                  <span className={headerAdjacentProjectArrowClass} aria-hidden>
                    ←
                  </span>
                  <span className="truncate min-w-0">{projectNav.prev.title}</span>
                  <span className={headerAdjacentProjectUnderlineClass} aria-hidden />
                </Link>
                <span className={headerAdjacentProjectDotClass} aria-hidden>
                  ·
                </span>
                <Link
                  to={projectNav.next.to}
                  className={`${headerAdjacentProjectLinkClass} justify-start`}
                  title={`Next project · ${projectNav.next.title}`}
                  data-cursor="pointer"
                  aria-label={`Next project: ${projectNav.next.title}`}
                >
                  <span className="truncate min-w-0">{projectNav.next.title}</span>
                  <span className={headerAdjacentProjectArrowClass} aria-hidden>
                    →
                  </span>
                  <span className={headerAdjacentProjectUnderlineClass} aria-hidden />
                </Link>
              </nav>
            )}
          </div>

          {/* RIGHT — nav */}
          <div className="flex items-center justify-end gap-8">
            <nav className="hidden lg:flex items-center gap-8">
              <a
                href="/"
                onClick={handleWorkClick}
                data-cursor="pointer"
                className={headerPrimaryNavLinkClass(isWorkActive)}
              >
                Work
              </a>
              {/* Tracking is applied after the last letter as well, so the
                  link box sits on the gutter while the "t" stops short of it.
                  Pulling back by exactly one letter-space aligns the glyph
                  with the page edge, and with everything else set flush right. */}
              <NavLink
                to="/About"
                data-cursor="pointer"
                className={`${headerPrimaryNavLinkClass(isAboutActive)} -mr-[0.18em]`}
              >
                About
              </NavLink>
            </nav>

            <button
              onClick={toggleMenu}
              className="lg:hidden flex flex-col justify-center items-center w-6 h-6 clickable"
              data-cursor="pointer"
              aria-label="Toggle menu"
            >
              <span
                className={`
                  block w-5 h-[1.5px] bg-black dark:bg-white transition-all duration-300
                  ${menuOpen ? 'rotate-45 translate-y-[3px]' : ''}
                `}
              />
              <span
                className={`
                  block w-5 h-[1.5px] bg-black dark:bg-white mt-[5px] transition-all duration-300
                  ${menuOpen ? '-rotate-45 -translate-y-[3px]' : ''}
                `}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className={`
          fixed inset-0 z-40 lg:hidden
          bg-white dark:bg-black
          flex flex-col items-center justify-center
          transition-all duration-500 ease-out
          ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        <nav className="flex flex-col items-center gap-8 px-8 max-w-full">
          <a
            href="/"
            onClick={handleWorkClick}
            className={headerMobilePrimaryLinkClass}
            data-cursor="pointer"
          >
            Work
          </a>
          <Link
            to="/About"
            onClick={closeMenu}
            className={headerMobilePrimaryLinkClass}
            data-cursor="pointer"
          >
            About
          </Link>

          {projectNav && (
            <div className="mt-4 pt-8 border-t border-border dark:border-neutral-800 w-full max-w-sm flex flex-col items-center gap-4">
              <span className={headerMobileSectionLabelClass}>
                Projects
              </span>
              <Link
                to={projectNav.prev.to}
                onClick={closeMenu}
                className={headerMobileAdjacentProjectLinkClass}
                title={projectNav.prev.title}
                data-cursor="pointer"
              >
                ← {projectNav.prev.title}
              </Link>
              <Link
                to={projectNav.next.to}
                onClick={closeMenu}
                className={headerMobileAdjacentProjectLinkClass}
                title={projectNav.next.title}
                data-cursor="pointer"
              >
                {projectNav.next.title} →
              </Link>
            </div>
          )}
        </nav>
      </div>
    </>
  );
};

export default Header;
